/**
 * バルーンコンポーネント（SVG内で使用）
 */

import { splitTextIntoLines, calculateTextWidth } from './utils';

/**
 * エラーバルーンコンポーネント（SVG内で使用）
 * 文章の長さに応じてサイズを自動調整、日本語対応
 */
export function ErrorBalloon({ x, y, message }: { x: number; y: number; message: string }) {
  // 文字幅設定
  const charWidth = 5.5;    // 半角英数
  const jpCharWidth = 10;   // 全角日本語
  const padding = 10;
  const lineHeight = 15;
  const maxWidth = 200;     // 最大幅

  // テキストを行に分割
  const lines = splitTextIntoLines(message, maxWidth, charWidth, jpCharWidth);

  // 各行の幅を計算し、最大幅を取得
  const maxLineWidth = Math.max(
    ...lines.map(line => calculateTextWidth(line, charWidth, jpCharWidth)),
    60 // 最小幅
  );

  const width = Math.min(maxLineWidth + padding * 2, maxWidth + padding * 2);
  const height = lines.length * lineHeight + padding * 1.5;
  const tailSize = 6;

  return (
    <g className="error-balloon">
      {/* バルーン本体 */}
      <rect
        x={x}
        y={y - height - tailSize}
        width={width}
        height={height}
        rx="4"
        fill="hsl(var(--destructive))"
      />
      {/* 吹き出しの尾（左側、先端が左向き） */}
      <polygon
        points={`${x + 10},${y - tailSize} ${x + 4},${y} ${x + 20},${y - tailSize}`}
        fill="hsl(var(--destructive))"
      />
      {/* テキスト（複数行対応） */}
      {lines.map((line, idx) => (
        <text
          key={idx}
          x={x + padding}
          y={y - height - tailSize + padding + (idx + 0.7) * lineHeight}
          className="text-[9px] fill-white font-medium"
        >
          {line}
        </text>
      ))}
    </g>
  );
}

/**
 * コストバルーンコンポーネント（SVG内で使用）
 * 配置コストを表示（濃い青線、白背景、黒文字）
 */
export function CostBalloon({ x, y, cost }: { x: number; y: number; cost: number }) {
  const text = `Cost: $${cost}`;
  const charWidth = 6;
  const textWidth = text.length * charWidth;
  const padding = 8;
  const height = 22;
  const tailSize = 6;
  const width = textWidth + padding * 2;

  return (
    <g className="cost-balloon">
      {/* バルーン本体（白背景、濃い青線） */}
      <rect
        x={x}
        y={y - height - tailSize}
        width={width}
        height={height}
        rx="4"
        fill="white"
        stroke="hsl(210, 70%, 40%)"
        strokeWidth="2"
      />
      {/* 吹き出しの尾（左側、先端が左向き） */}
      <polygon
        points={`${x + 10},${y - tailSize} ${x + 0},${y} ${x + 20},${y - tailSize}`}
        fill="white"
        stroke="hsl(210, 70%, 40%)"
        strokeWidth="2"
      />
      {/* 尾の内側（白で塗りつぶし） */}
      <polygon
        points={`${x + 11},${y - tailSize - 1} ${x + 4},${y - 2} ${x + 19},${y - tailSize - 1}`}
        fill="white"
      />
      {/* テキスト（黒文字） */}
      <text
        x={x + padding}
        y={y - height - tailSize + 15}
        className="text-[10px] fill-foreground font-semibold"
      >
        {text}
      </text>
    </g>
  );
}

/**
 * Incomeバルーンコンポーネント（SVG内で使用）
 * 輸送時のIncome上昇を表示（緑背景、白文字）
 */
export function IncomeBalloon({ x, y, income }: { x: number; y: number; income: number }) {
  const text = `+${income} Income`;
  const charWidth = 7;
  const textWidth = text.length * charWidth;
  const padding = 10;
  const height = 28;
  const tailSize = 8;
  const width = textWidth + padding * 2;

  return (
    <g className="income-balloon">
      {/* バルーン本体（緑背景） */}
      <rect
        x={x}
        y={y - height - tailSize}
        width={width}
        height={height}
        rx="6"
        fill="hsl(142, 76%, 36%)"
        stroke="hsl(142, 76%, 26%)"
        strokeWidth="2"
      />
      {/* 吹き出しの尾（左側、先端が左向き） */}
      <polygon
        points={`${x + 10},${y - tailSize} ${x + 0},${y} ${x + 24},${y - tailSize}`}
        fill="hsl(142, 76%, 36%)"
        stroke="hsl(142, 76%, 26%)"
        strokeWidth="2"
      />
      {/* 尾の内側（緑で塗りつぶし） */}
      <polygon
        points={`${x + 12},${y - tailSize - 1} ${x + 4},${y - 3} ${x + 22},${y - tailSize - 1}`}
        fill="hsl(142, 76%, 36%)"
      />
      {/* テキスト（白文字、太字） */}
      <text
        x={x + padding}
        y={y - height - tailSize + 19}
        className="text-[12px] fill-white font-bold"
      >
        {text}
      </text>
    </g>
  );
}
